import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import api from '../../lib/api';
import { useRealtime } from '../../lib/realtime';
import { fmtEur } from '../../lib/constants';
import { Button } from '../ui/button';
import { ScrollArea } from '../ui/scroll-area';
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuTrigger,
} from '../ui/dropdown-menu';
import { Bell, BellRing, Check, X, Smartphone } from 'lucide-react';

export const RemoteOrdersBell = () => {
  const { t } = useTranslation();
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(null);
  const prevCount = useRef(0);

  const load = useCallback(async (notify = false) => {
    try {
      const { data } = await api.get('/remote-orders', { params: { status: 'pending' } });
      if (notify && data.length > prevCount.current) {
        toast.info(t('remoteOrders.newOrder'));
      }
      prevCount.current = data.length;
      setItems(data);
    } catch (e) { /* noop */ }
  }, [t]);

  useEffect(() => {
    load();
    const id = setInterval(() => load(false), 25000);
    return () => clearInterval(id);
  }, [load]);

  useRealtime((msg) => {
    if (msg?.event === 'remote_orders') load(true);
  });

  const act = async (ro, action) => {
    setBusy(ro.id);
    try {
      await api.post(`/remote-orders/${ro.id}/${action}`);
      toast.success(action === 'accept' ? t('remoteOrders.accepted') : t('remoteOrders.rejected'));
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Errore');
      await load();
    } finally { setBusy(null); }
  };

  const count = items.length;

  return (
    <DropdownMenu open={open} onOpenChange={setOpen}>
      <DropdownMenuTrigger asChild>
        <Button variant="ghost" size="icon" className="relative" data-testid="remote-orders-bell" aria-label={t('remoteOrders.title')}>
          {count > 0 ? <BellRing className="h-5 w-5 text-primary" /> : <Bell className="h-5 w-5" />}
          {count > 0 && (
            <span data-testid="remote-orders-badge" className="absolute -top-0.5 -right-0.5 min-w-[18px] h-[18px] px-1 rounded-full bg-destructive text-destructive-foreground text-[10px] font-bold flex items-center justify-center">
              {count > 99 ? '99+' : count}
            </span>
          )}
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-[340px] p-0" data-testid="remote-orders-panel">
        <div className="px-3 py-2.5 border-b border-border flex items-center gap-2">
          <Smartphone className="h-4 w-4 text-primary" />
          <span className="font-semibold text-sm">{t('remoteOrders.title')}</span>
          {count > 0 && <span className="ml-auto text-xs text-muted-foreground">{count}</span>}
        </div>
        {count === 0 ? (
          <div className="px-3 py-8 text-center text-sm text-muted-foreground" data-testid="remote-orders-empty">{t('remoteOrders.empty')}</div>
        ) : (
          <ScrollArea className="max-h-[420px]">
            <div className="divide-y divide-border">
              {items.map((ro) => (
                <div key={ro.id} className="p-3" data-testid={`remote-order-${ro.id}`}>
                  <div className="flex items-center justify-between gap-2">
                    <div className="font-display font-bold">{t('remoteOrders.table')} {ro.table_name}</div>
                    <div className="font-display font-black text-primary tabular">{fmtEur(ro.total)}</div>
                  </div>
                  <div className="text-xs text-muted-foreground mt-0.5">
                    {ro.customer_name ? `${ro.customer_name} · ` : ''}{ro.items.length} {t('remoteOrders.items')}
                  </div>
                  <ul className="mt-2 space-y-0.5 text-sm">
                    {ro.items.map((it) => (
                      <li key={it.id} className="flex justify-between gap-2">
                        <span className="truncate">{it.qty}× {it.name}{it.variant ? ` · ${it.variant}` : ''}</span>
                      </li>
                    ))}
                  </ul>
                  {ro.note && <div className="mt-1.5 text-xs italic text-amber-600 dark:text-amber-400">⚠ {ro.note}</div>}
                  <div className="mt-2.5 flex gap-2">
                    <Button size="sm" className="flex-1 rounded-lg" disabled={busy === ro.id} onClick={() => act(ro, 'accept')} data-testid={`remote-accept-${ro.id}`}>
                      <Check className="h-4 w-4 mr-1" /> {t('remoteOrders.accept')}
                    </Button>
                    <Button size="sm" variant="outline" className="flex-1 rounded-lg text-destructive" disabled={busy === ro.id} onClick={() => act(ro, 'reject')} data-testid={`remote-reject-${ro.id}`}>
                      <X className="h-4 w-4 mr-1" /> {t('remoteOrders.reject')}
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          </ScrollArea>
        )}
      </DropdownMenuContent>
    </DropdownMenu>
  );
};
